import { memo, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Form, Input, Modal, Space } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'translations';
import ShowError from 'components/alerts/ShowError';
import { useRequest } from '@wappsto/blanket';
import ReCAPTCHA from 'react-google-recaptcha';
import getConfigFile from 'configFiles';
import { useDispatch } from 'react-redux';
import { addSession, REQUEST_ERROR } from '@wappsto/redux';
import Cookies from 'util/cookies';
import ForgotPassword from 'features/requestPasswordRecovery';

const LoginForm = memo(() => {
  const { tC } = useTranslation('features', 'authentication');
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { request, send } = useRequest();
  const [loading, setLoading] = useState(false);
  const [recaptcha, setRecaptcha] = useState('');
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [showError, setShowError] = useState(false);
  const errorNumber = useRef(0);
  const alwaysShowRecaptcha = errorNumber.current > 2;
  const [recaptchaConfig, setRecaptchaConfig] = useState<any>('LOADING');

  useEffect(() => {
    getConfigFile('recaptcha').then(setRecaptchaConfig);
  }, []);

  const handleSubmit = (values: any, rc?: any) => {
    setShowError(false);
    setLoading(true);
    send({
      method: 'POST',
      url: '/session',
      body: {
        username: values.username,
        password: values.password,
        captcha: rc || recaptcha,
        remember_me: values.remember,
      },
    });
  };

  const cancelRecaptcha = () => {
    errorNumber.current = 3;
    setLoading(false);
    setShowRecaptcha(false);
  };

  const handleModalRecaptcha = (rc: any) => {
    setShowRecaptcha(false);
    setRecaptcha(rc);
    handleSubmit(form.getFieldsValue(), rc);
  };

  useEffect(() => {
    if (request) {
      if (request.status === 'success') {
        if (request.json) {
          dispatch(addSession(request.json));
          Cookies.create('sessionID', request.json.meta.id, 30);
        } else {
          dispatch({
            type: REQUEST_ERROR,
            ...request,
          });
        }
      } else if (request.status === 'error') {
        if (
          !alwaysShowRecaptcha &&
          !showRecaptcha &&
          request.json &&
          request.json.code === 9900007
        ) {
          setShowRecaptcha(true);
        } else {
          errorNumber.current += 1;
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setLoading(false);
          setShowError(true);
          setRecaptcha('');
        }
      }
    }
  }, [request, alwaysShowRecaptcha, dispatch, showRecaptcha]);

  return (
    <>
      <Form
        form={form}
        className="login-form"
        name="login"
        layout="vertical"
        initialValues={{ remember: true }}
        onFinish={(values) => handleSubmit(values)}>
        <Form.Item
          label={tC('username')}
          name="username"
          rules={[{ required: true, type: 'email' }]}>
          <Input prefix={<UserOutlined className="site-form-item-icon" />} />
        </Form.Item>
        <Form.Item label={tC('password')} name="password" rules={[{ required: true }]}>
          <Input.Password prefix={<LockOutlined className="site-form-item-icon" />} />
        </Form.Item>
        {alwaysShowRecaptcha && !showRecaptcha && request?.status !== 'success' && (
          <Form.Item className="recaptcha">
            {recaptchaConfig === 'LOADING' ? (
              <p>Loading...</p>
            ) : (
              <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaConfig.key} onChange={setRecaptcha} />
            )}
          </Form.Item>
        )}
        <Form.Item>
          <Space align="start" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Form.Item name="remember" valuePropName="checked">
              <Checkbox>{tC('rememberMe')}</Checkbox>
            </Form.Item>

            <ForgotPassword />
          </Space>
        </Form.Item>
        {showError && <ShowError request={request} />}
        <Form.Item>
          <Button
            type="primary"
            size="large"
            block
            htmlType="submit"
            disabled={alwaysShowRecaptcha && !recaptcha}
            loading={loading}>
            {tC('loginButton')}
          </Button>
        </Form.Item>
      </Form>

      <Modal open={showRecaptcha} onCancel={cancelRecaptcha} footer={null} destroyOnHidden>
        <ReCAPTCHA sitekey={recaptchaConfig.key} onChange={handleModalRecaptcha} />
      </Modal>
    </>
  );
});

LoginForm.displayName = 'LoginForm';

export default LoginForm;
